import { v4 as uuidv4 } from "uuid";
import { getActivePinia } from "pinia";
import { hydrateStore } from "@/store/utils";

interface TabMessage {
  type: "open" | "open_ack" | "close";
  id: string;
}

const id = uuidv4();
const otherTabs = new Set<string>();
let channel: BroadcastChannel | null = null;

function isSessionKey(key: string) {
  return key === "root" || key === "offer" || key.startsWith("oidc.user:");
}

function openChannel() {
  channel = new BroadcastChannel("ezra-tab-channel");
  channel.onmessage = ({ data }: MessageEvent<TabMessage>) => {
    if (data.id === id) return;
    if (data.type === "open") {
      otherTabs.add(data.id);
      channel?.postMessage({ type: "open_ack", id });
    } else if (data.type === "open_ack") {
      otherTabs.add(data.id);
    } else if (data.type === "close") {
      otherTabs.delete(data.id);
    }
  };
  channel.postMessage({ type: "open", id });
}

// The session must not outlive the last tab, so the last tab moves it into its own
// sessionStorage, where only a reload of that tab can restore it.
function saveStorage() {
  if (otherTabs.size > 0) return;
  Object.keys(localStorage)
    .filter(isSessionKey)
    .forEach((key, i) => {
      const value = localStorage.getItem(key);
      if (value === null) return;
      sessionStorage.setItem(`tmp-${key}`, value);
      sessionStorage.setItem(`restoreKeys-${i}`, key);
      localStorage.removeItem(key);
    });
}

// A credential means another tab has signed in; a signed-out root store means one signed out.
// Bringing the saved session back would then mix sessions or undo that sign-out.
function sessionChangedElsewhere() {
  if (Object.keys(localStorage).some(key => key.startsWith("oidc.user:"))) return true;
  const root = localStorage.getItem("root");
  try {
    return root !== null && JSON.parse(root).authenticated === false;
  } catch {
    return true;
  }
}

function restoreStorage() {
  const restore = !sessionChangedElsewhere();
  Object.keys(sessionStorage)
    .filter(k => k.startsWith("restoreKeys"))
    .forEach(restoreKey => {
      const key = sessionStorage.getItem(restoreKey) ?? "";
      const value = sessionStorage.getItem(`tmp-${key}`);
      if (restore && value && !localStorage.getItem(key)) localStorage.setItem(key, value);
      sessionStorage.removeItem(`tmp-${key}`);
      sessionStorage.removeItem(restoreKey);
    });
}

export function startTabTracking() {
  restoreStorage();
  openChannel();
  // Unlike beforeunload, pagehide doesn't fire for downloads, mailto: links or cancelled
  // navigations, which leave the page running.
  window.addEventListener("pagehide", () => {
    channel?.postMessage({ type: "close", id });
    channel?.close();
    channel = null;
    saveStorage();
  });
  // A page restored from the back/forward cache resumes without booting, and missed any
  // "close" messages while it was cached.
  window.addEventListener("pageshow", event => {
    if (!event.persisted) return;
    otherTabs.clear();
    openChannel();
    restoreStorage();
    // Before main.ts installs Pinia no store exists yet; each one hydrates when it's created.
    if (getActivePinia()) hydrateStore();
  });
}
